import React, { useState, useRef } from 'react';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import { 
  Pencil, 
  Eraser, 
  Undo2, 
  Redo2, 
  Trash2, 
  Maximize2, 
  X, 
  Download,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const PLANTILLAS = [
  { id: 'none', label: 'Sin Plantilla', icon: null },
  { id: 'male_front', label: 'Silueta Masculina Frontal', icon: '👤' },
  { id: 'male_back', label: 'Silueta Posterior', icon: '🚶' },
  { id: 'blazer', label: 'Blazer', icon: '🧥' },
  { id: 'shirt', label: 'Camisa', icon: '👔' },
  { id: 'pants', label: 'Pantalón', icon: '👖' }
];

const COLORES = [
  '#000000', '#666666', '#999999', '#CCCCCC',
  '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
  '#FF00FF', '#00FFFF', '#FFA500', '#800080'
];

const GROSORES = [2, 4, 6, 8, 12, 16];

export const SketchCanvas = ({ initialSketch, onSave }) => {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('draw');
  const [color, setColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [description, setDescription] = useState(initialSketch?.description || '');

  React.useEffect(() => {
    if (initialSketch?.data && canvasRef.current) {
      try {
        // Parse the data if it's a string (saved from JSON.stringify)
        const pathsData = typeof initialSketch.data === 'string' 
          ? JSON.parse(initialSketch.data) 
          : initialSketch.data;
        
        // Only load if pathsData is a valid array
        if (Array.isArray(pathsData) && pathsData.length > 0) {
          canvasRef.current.loadPaths(pathsData);
        }
      } catch (error) {
        console.error('Error loading sketch:', error);
      }
    }
  }, [initialSketch]);

  const handleUndo = () => {
    if (canvasRef.current) {
      canvasRef.current.undo();
    }
  };

  const handleRedo = () => {
    if (canvasRef.current) {
      canvasRef.current.redo();
    }
  };

  const handleClear = () => {
    if (window.confirm('¿Limpiar todo el dibujo?')) {
      if (canvasRef.current) {
        canvasRef.current.clearCanvas();
      }
    }
  };

  const handleSave = async () => {
    if (canvasRef.current) {
      try {
        const paths = await canvasRef.current.exportPaths();
        const imageData = await canvasRef.current.exportImage('png');
        
        const sketchData = {
          data: JSON.stringify(paths),
          imagePreview: imageData,
          description: description
        };
        
        onSave(sketchData);
        toast.success('Croquis guardado');
      } catch (error) {
        console.error('Error saving sketch:', error);
        toast.error('Error al guardar el croquis');
      }
    }
  };

  const handleDownload = async () => {
    if (canvasRef.current) {
      try {
        const imageData = await canvasRef.current.exportImage('png');
        const link = document.createElement('a');
        link.href = imageData;
        link.download = `croquis_${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Imagen descargada');
      } catch (error) {
        toast.error('Error al descargar');
      }
    }
  };

  const ToolButton = ({ icon: Icon, label, onClick, active, color: btnColor }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-3 border rounded-none text-xs transition-all ${
        active
          ? 'bg-black text-white border-black'
          : 'bg-white text-black border-border hover:border-black'
      }`}
      title={label}
      data-testid={`sketch-tool-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="w-5 h-5 mb-1" style={btnColor ? { color: btnColor } : {}} />
      <span className="hidden md:inline">{label}</span>
    </button>
  );

  const canvasContent = (
    <div className={`${isFullscreen ? 'fixed inset-0 bg-white z-50 p-8' : ''}`}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border">
          <div className="flex flex-wrap items-center gap-2">
            <ToolButton
              icon={Pencil}
              label="Dibujar"
              onClick={() => {
                setMode('draw');
                if (canvasRef.current) canvasRef.current.eraseMode(false);
              }}
              active={mode === 'draw'}
            />
            <ToolButton
              icon={Eraser}
              label="Borrador"
              onClick={() => {
                setMode('erase');
                if (canvasRef.current) canvasRef.current.eraseMode(true);
              }}
              active={mode === 'erase'}
            />
            <div className="w-px h-8 bg-border mx-2" />
            <ToolButton icon={Undo2} label="Deshacer" onClick={handleUndo} />
            <ToolButton icon={Redo2} label="Rehacer" onClick={handleRedo} />
            <ToolButton icon={Trash2} label="Limpiar" onClick={handleClear} />
            <div className="w-px h-8 bg-border mx-2" />
            <ToolButton icon={Download} label="Descargar" onClick={handleDownload} />
          </div>

          <div className="flex items-center gap-2">
            {!isFullscreen ? (
              <button
                type="button"
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-2 px-4 h-10 border border-border bg-white text-black hover:border-black rounded-none text-xs uppercase tracking-widest transition-all"
                data-testid="sketch-fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
                <span className="hidden md:inline">Pantalla Completa</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="flex items-center gap-2 px-4 h-10 bg-black text-white hover:bg-zinc-800 rounded-none text-xs uppercase tracking-widest transition-all"
                data-testid="sketch-exit-fullscreen"
              >
                <X className="w-4 h-4" />
                Cerrar
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Color del Trazo
            </label>
            <div className="flex flex-wrap gap-2">
              {COLORES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                    color === c ? 'border-black scale-110' : 'border-border'
                  }`}
                  style={{ backgroundColor: c }}
                  title={c}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Grosor del Trazo
            </label>
            <div className="flex flex-wrap gap-2">
              {GROSORES.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setStrokeWidth(w)}
                  className={`px-4 h-10 border rounded-none text-xs transition-all ${
                    strokeWidth === w
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-black border-border hover:border-black'
                  }`}
                  data-testid={`width-${w}`}
                >
                  {w}px
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
            Plantilla Base (Opcional)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {PLANTILLAS.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedTemplate(template.id)}
                className={`flex items-center justify-center gap-2 p-3 border rounded-none text-xs transition-all ${
                  selectedTemplate === template.id
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-border hover:border-black'
                }`}
                data-testid={`template-${template.id}`}
              >
                {template.icon && <span className="text-lg">{template.icon}</span>}
                <span className="hidden lg:inline">{template.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={`relative bg-zinc-50 border-2 border-border ${isFullscreen ? 'h-[calc(100vh-500px)]' : 'h-96'}`}>
          {selectedTemplate !== 'none' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 z-0">
              <div className="text-9xl">{PLANTILLAS.find(t => t.id === selectedTemplate)?.icon}</div>
            </div>
          )}
          <ReactSketchCanvas
            ref={canvasRef}
            strokeColor={color}
            strokeWidth={strokeWidth}
            eraserWidth={strokeWidth * 2}
            canvasColor="transparent"
            style={{
              border: 'none',
              cursor: mode === 'draw' ? 'crosshair' : 'pointer'
            }}
            data-testid="sketch-canvas"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
            Descripción del Croquis
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="3"
            placeholder="Ej: Blazer cruzado con botones dorados, solapas anchas, corte slim..."
            className="w-full rounded-none border border-input bg-white px-4 py-3 text-sm focus:outline-none focus:border-black resize-none"
            data-testid="sketch-description"
          />
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="w-full bg-black text-white hover:bg-zinc-800 rounded-none h-12 uppercase tracking-widest text-xs font-medium transition-all"
          data-testid="save-sketch"
        >
          Guardar Croquis
        </button>
      </div>
    </div>
  );

  return canvasContent;
};
